<template>
  <div class="admin-page">
    <div class="admin-container">
      <h1>Admin Panel</h1>
      
      <!-- Tabs -->
      <div class="tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="switchTab(tab.id)"
          :class="['tab-button', { active: activeTab === tab.id }]"
        >
          {{ tab.label }}
        </button>
      </div>

      <!-- CSV Import Section (Courses tab only) -->
      <div v-if="activeTab === 'courses' && !loading && !error" class="csv-import-section">
        <div class="csv-import-header">
          <h2>Import Courses from CSV</h2>
          <p class="csv-import-description">
            Upload a CSV file to import multiple courses at once. The system will automatically create providers if they don't exist.
          </p>
        </div>
        <div class="csv-import-controls">
          <input
            type="file"
            ref="fileInput"
            @change="handleFileSelect"
            accept=".csv"
            class="file-input"
            :disabled="importing"
          />
          <button
            @click="triggerFileInput"
            :disabled="importing"
            class="select-file-button"
          >
            Select CSV File
          </button>
          <button
            @click="handleImport"
            :disabled="importing || !selectedFile"
            class="import-button"
          >
            {{ importing ? 'Importing...' : 'Import CSV' }}
          </button>
        </div>
        <div v-if="selectedFile" class="selected-file">
          Selected: {{ selectedFile.name }}
        </div>
        <div v-if="importResult" class="import-result" :class="{ success: importResult.success, error: !importResult.success }">
          <h3>{{ importResult.success ? 'Import Completed' : 'Import Failed' }}</h3>
          <p v-if="importResult.success">
            Successfully imported {{ importResult.success_count }} courses.
            <span v-if="importResult.failure_count > 0">
              {{ importResult.failure_count }} failed.
            </span>
          </p>
          <div v-if="importResult.errors && importResult.errors.length > 0" class="import-errors">
            <h4>Errors:</h4>
            <ul>
              <li v-for="(error, index) in importResult.errors" :key="index">{{ error }}</li>
            </ul>
          </div>
          <button v-if="importResult.success" @click="handleImportSuccess" class="refresh-button">
            Refresh Data
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading">
        <p>Loading {{ getActiveTabLabel() }}...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error">
        <p>Error loading data: {{ error }}</p>
        <button @click="loadData" class="retry-button">Retry</button>
      </div>

      <!-- Table -->
      <div v-else class="table-container">
        <!-- Courses filters (courses tab only) -->
        <div v-if="activeTab === 'courses'" class="courses-filters">
          <div class="courses-search-row">
            <label for="coursesSearch">Search:</label>
            <input
              id="coursesSearch"
              v-model="coursesSearchQuery"
              type="text"
              class="courses-search-input"
              placeholder="Name, description, instructor, provider..."
              @keyup.enter="applyCoursesFilters"
            />
            <button type="button" class="courses-search-button" @click="applyCoursesFilters">
              Search
            </button>
          </div>
          <div class="courses-filter-row">
            <div class="status-filter-section">
              <label for="coursesStatusFilter">Show:</label>
              <select id="coursesStatusFilter" v-model="coursesStatusFilter" class="status-filter-select" @change="onCoursesFilterChange">
                <option value="all_except_inactive">All except Inactive</option>
                <option value="active">Active only</option>
                <option value="in_progress">In progress (Needs Review / Info Missing)</option>
                <option value="needs_review">Needs Review only</option>
                <option value="info_missing">Info Missing only</option>
                <option value="inactive">Inactive only</option>
                <option value="any">All statuses</option>
              </select>
            </div>
            <div class="activity-type-filter-section">
              <label for="coursesActivityType">Activity type:</label>
              <select id="coursesActivityType" v-model="coursesActivityTypeFilter" class="filter-select" @change="onCoursesFilterChange">
                <option value="">All types</option>
                <option v-for="type in courseTypes" :key="type" :value="type">
                  {{ type }}
                </option>
              </select>
            </div>
            <div class="provider-filter-section">
              <label for="coursesProvider">Provider:</label>
              <select id="coursesProvider" v-model="coursesProviderFilter" class="filter-select" @change="onCoursesFilterChange">
                <option value="">All providers</option>
                <option v-for="p in providerListForFilter" :key="p.id" :value="p.id">
                  {{ p.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <!-- Create New Button -->
        <div v-if="activeTab !== 'reviews'" class="create-new-section">
          <button @click="createNew" class="create-new-button">
            Create New {{ getActiveTabLabel().slice(0, -1) }}
          </button>
        </div>

        <table class="admin-table">
          <thead>
            <tr>
              <th v-for="column in getColumns()" :key="column.key">
                {{ column.label }}
              </th>
              <th class="actions-column">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="paginatedData.length === 0">
              <td :colspan="getColumns().length + 1" class="empty-state">
                No {{ getActiveTabLabel().toLowerCase() }} found
              </td>
            </tr>
            <tr v-for="item in paginatedData" :key="item.id" class="table-row">
              <td v-for="column in getColumns()" :key="column.key">
                <span v-if="activeTab === 'courses' && column.key === 'status'" class="status-cell status-cell--editable">
                  <span :class="['status-dot', getStatusDotClass(item.status)]" aria-hidden="true"></span>
                  <select
                    :value="item.status"
                    :disabled="updatingStatusCourseId === item.id"
                    class="status-select"
                    @change="onCourseStatusChange(item, $event.target.value)"
                  >
                    <option v-for="opt in courseStatusOptions" :key="opt.value" :value="opt.value">
                      {{ opt.label }}
                    </option>
                  </select>
                </span>
                <template v-else>
                  {{ formatCellValue(item, column.key) }}
                </template>
              </td>
              <td class="actions-cell">
                <button @click="editItem(item)" class="edit-button">
                  Edit
                </button>
                <button @click="deleteItem(item)" class="delete-button">
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="pagination">
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            class="page-button"
          >
            Previous
          </button>
          <span class="page-info">
            Page {{ currentPage }} of {{ totalPages }} ({{ totalItems }} total)
          </span>
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            class="page-button"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCoursesPaginated, importCoursesFromCSV, deleteCourse, updateCourseStatus } from '../../services/courseService.js'
import { COURSE_STATUSES, getCourseTypes } from '../../models/Course.js'
import { getReviewsPaginated, deleteReview } from '../../services/reviewService.js'
import { getProvidersPaginated, deleteProvider } from '../../services/providerService.js'

export default {
  name: 'AdminPage',
  data() {
    return {
      activeTab: 'courses',
      tabs: [
        { id: 'courses', label: 'Courses' },
        { id: 'reviews', label: 'Reviews' },
        { id: 'providers', label: 'Providers' }
      ],
      courses: [],
      reviews: [],
      providers: [],
      pagination: {
        page: 1,
        per_page: 20,
        total: 0,
        total_pages: 0
      },
      loading: false,
      error: null,
      currentPage: 1,
      pageSize: 20,
      selectedFile: null,
      importing: false,
      importResult: null,
      coursesStatusFilter: 'all_except_inactive',
      updatingStatusCourseId: null,
      coursesSearchQuery: '',
      coursesActivityTypeFilter: '',
      coursesProviderFilter: '',
      providerListForFilter: []
    }
  },
  computed: {
    courseStatusOptions() {
      return COURSE_STATUSES
    },
    courseTypes() {
      return getCourseTypes()
    },
    currentData() {
      switch (this.activeTab) {
        case 'courses':
          return this.courses
        case 'reviews':
          return this.reviews
        case 'providers':
          return this.providers
        default:
          return []
      }
    },
    totalItems() {
      return this.pagination.total || 0
    },
    totalPages() {
      return this.pagination.total_pages || 0
    },
    paginatedData() {
      // Data is already paginated from the backend
      return this.currentData
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    switchTab(tabId) {
      this.activeTab = tabId
      this.currentPage = 1
      this.loadData()
    },
    async loadData() {
      this.loading = true
      this.error = null

      try {
        let result
        switch (this.activeTab) {
        case 'courses': {
          const providerPromise = this.providerListForFilter.length
            ? Promise.resolve({ data: [] })
            : getProvidersPaginated({ page: 1, perPage: 500 })
          const [courseResult, providerResult] = await Promise.all([
            getCoursesPaginated({
              page: this.currentPage,
              perPage: this.pageSize,
              statusFilter: this.coursesStatusFilter,
              search: this.coursesSearchQuery,
              courseType: this.coursesActivityTypeFilter,
              providerId: this.coursesProviderFilter
            }),
            providerPromise
          ])
          this.courses = courseResult.data
          this.pagination = courseResult.pagination
          if (providerResult.data && providerResult.data.length) {
            this.providerListForFilter = providerResult.data
          }
          break
        }
          case 'reviews':
            result = await getReviewsPaginated({ page: this.currentPage, perPage: this.pageSize })
            this.reviews = result.data
            this.pagination = result.pagination
            break
          case 'providers':
            result = await getProvidersPaginated({ page: this.currentPage, perPage: this.pageSize })
            this.providers = result.data
            this.pagination = result.pagination
            break
        }
      } catch (error) {
        console.error('Error loading admin data:', error)
        this.error = error.message || 'Failed to load data'
      } finally {
        this.loading = false
      }
    },
    getActiveTabLabel() {
      const tab = this.tabs.find(t => t.id === this.activeTab)
      return tab ? tab.label : ''
    },
    getColumns() {
      switch (this.activeTab) {
        case 'courses':
          return [
            { key: 'name', label: 'Name' },
            { key: 'courseType', label: 'Type' },
            { key: 'providerName', label: 'Provider' },
            { key: 'status', label: 'Status' },
            { key: 'credits', label: 'Credits' },
            { key: 'cost', label: 'Cost' },
            { key: 'competencies', label: 'Competencies' }
          ]
        case 'reviews':
          return [
            { key: 'courseId', label: 'Course ID' },
            { key: 'userId', label: 'User ID' },
            { key: 'educationalContent', label: 'Education' },
            { key: 'engagement', label: 'Engagement' },
            { key: 'overallValue', label: 'Overall' },
            { key: 'reviewText', label: 'Review' },
            { key: 'createdAt', label: 'Created' }
          ]
        case 'providers':
          return [
            { key: 'name', label: 'Name' },
            { key: 'provider_type', label: 'Type' },
            { key: 'website', label: 'Website' },
            { key: 'city', label: 'City' },
            { key: 'state', label: 'State' },
            { key: 'created_at', label: 'Created' }
          ]
        default:
          return []
      }
    },
    formatCellValue(item, key) {
      const value = item[key]

      // Handle null/undefined
      if (value === null || value === undefined) {
        return '—'
      }

      // Handle dates
      if (key.includes('created') || key.includes('updated') || key.includes('date')) {
        if (!value) return '—'
        const date = new Date(value)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      }

      // Handle review text (truncate)
      if (key === 'reviewText' && typeof value === 'string') {
        return value.length > 50 ? value.substring(0, 50) + '...' : value
      }

      // Handle cost
      if (key === 'cost' && typeof value === 'number') {
        return value === 0 ? 'Free' : `$${value.toFixed(2)}`
      }

      // Handle competencies (array of { id, name })
      if (key === 'competencies' && Array.isArray(value)) {
        return value.length ? value.map(c => c.name).join(', ') : '—'
      }

      // Handle course type (values are already display-friendly)
      if (key === 'courseType') {
        return value || '—'
      }

      // Handle provider type
      if (key === 'provider_type') {
        return value || '—'
      }

      // Handle course status (label only; dot is rendered in template)
      if (key === 'status') {
        const statusLabels = { needs_review: 'Needs Review', info_missing: 'Info Missing', inactive: 'Inactive', active: 'Active' }
        return statusLabels[value] || value || '—'
      }

      // Handle website (make it a link if it's a URL)
      if (key === 'website' && typeof value === 'string' && value.startsWith('http')) {
        return value // We could make this a link, but for now just show the URL
      }

      return value
    },
    getStatusDotClass(status) {
      if (status === 'active') return 'status-dot--active'
      if (status === 'needs_review' || status === 'info_missing') return 'status-dot--in-progress'
      if (status === 'inactive') return 'status-dot--inactive'
      return 'status-dot--in-progress'
    },
    async onCourseStatusChange(course, newStatus) {
      if (course.status === newStatus) return
      this.updatingStatusCourseId = course.id
      try {
        const updated = await updateCourseStatus(course.id, newStatus)
        course.status = updated.status
      } catch (err) {
        console.error('Status update failed:', err)
      } finally {
        this.updatingStatusCourseId = null
      }
    },
    applyCoursesFilters() {
      this.currentPage = 1
      this.loadData()
    },
    onCoursesFilterChange() {
      this.currentPage = 1
      this.loadData()
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page
        this.loadData()
        // Scroll to top of table
        this.$nextTick(() => {
          const container = this.$el.querySelector('.table-container')
          if (container) {
            container.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    },
    createNew() {
      switch (this.activeTab) {
        case 'courses':
          this.$router.push({ name: 'add-course' })
          break
        case 'reviews':
          // Reviews need a course context, so navigate to search page
          this.$router.push({ name: 'search' })
          break
        case 'providers':
          this.$router.push({ name: 'add-provider' })
          break
      }
    },
    editItem(item) {
      switch (this.activeTab) {
        case 'courses':
          // Navigate to edit course page
          this.$router.push({ name: 'edit-course', params: { id: item.id } })
          break
        case 'reviews':
          // Navigate to the course page where the review can be edited
          if (item.courseId) {
            this.$router.push({ name: 'view-course', params: { id: item.courseId } })
          }
          break
        case 'providers':
          // Provider editing not yet implemented, but we can navigate to search
          // where they might be able to find and view the provider
          this.$router.push({ name: 'search' })
          break
      }
    },
    async deleteItem(item) {
      const entityName = this.activeTab === 'courses' ? 'course' : this.activeTab === 'reviews' ? 'review' : 'provider'
      const displayName = item.name || item.id
      if (!confirm(`Are you sure you want to delete this ${entityName}?${displayName !== item.id ? `\n\n"${displayName}"` : ''}`)) {
        return
      }
      try {
        let deleted = false
        switch (this.activeTab) {
          case 'courses':
            deleted = await deleteCourse(item.id)
            break
          case 'reviews':
            deleted = await deleteReview(item.id)
            break
          case 'providers':
            deleted = await deleteProvider(item.id)
            break
        }
        if (deleted !== false) {
          await this.loadData()
        }
      } catch (error) {
        // ErrorModal is shown by handleApiError in the services
        console.error('Delete failed:', error)
      }
    },
    triggerFileInput() {
      this.$refs.fileInput?.click()
    },
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        if (!file.name.endsWith('.csv')) {
          alert('Please select a CSV file')
          return
        }
        this.selectedFile = file
        this.importResult = null
      }
    },
    async handleImport() {
      if (!this.selectedFile) return

      this.importing = true
      this.importResult = null
      this.error = null

      try {
        const result = await importCoursesFromCSV(this.selectedFile)
        this.importResult = {
          success: true,
          success_count: result.success_count || 0,
          failure_count: result.failure_count || 0,
          errors: result.errors || []
        }
        // Clear selected file after successful import
        if (this.importResult.success && this.importResult.failure_count === 0) {
          this.selectedFile = null
          if (this.$refs.fileInput) {
            this.$refs.fileInput.value = ''
          }
        }
      } catch (error) {
        console.error('Import error:', error)
        this.importResult = {
          success: false,
          errors: error.data?.errors || [error.message || 'Failed to import CSV']
        }
      } finally {
        this.importing = false
      }
    },
    handleImportSuccess() {
      // Refresh the data and reset import state
      this.importResult = null
      this.selectedFile = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
      this.loadData()
    }
  }
}
</script>

<style scoped>
.admin-page {
  min-height: calc(100vh - 200px);
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.admin-container {
  max-width: 1400px;
  width: 100%;
  background-color: var(--color-surface-light);
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

h1 {
  color: var(--color-text-primary);
  font-size: 2rem;
  margin-bottom: 30px;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 2px solid var(--color-border-light);
}

.tab-button {
  padding: 12px 24px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--color-text-secondary);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all var(--transition-normal);
  margin-bottom: -2px;
}

.tab-button:hover {
  color: var(--color-primary-main);
  background-color: var(--color-primary-light);
  background-color: transparent;
}

.tab-button.active {
  color: var(--color-primary-main);
  border-bottom-color: var(--color-primary-main);
  font-weight: 600;
}

/* Loading and Error States */
.loading,
.error {
  text-align: center;
  padding: 40px;
  color: var(--color-text-secondary);
}

.error {
  color: var(--color-error-main);
}

.retry-button {
  margin-top: 15px;
  padding: 10px 20px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  transition: all var(--transition-normal);
}

.retry-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

/* Table */
.table-container {
  overflow-x: auto;
  margin-top: 20px;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
  background-color: var(--color-surface-light);
}

.admin-table thead {
  background-color: var(--color-primary-light);
}

.admin-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: var(--color-text-primary);
  border-bottom: 2px solid var(--color-border-main);
  white-space: nowrap;
}

.admin-table td {
  padding: 12px 16px;
  border-bottom: 1px solid var(--color-border-light);
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.admin-table tbody tr:hover {
  background-color: var(--color-surface-dark);
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--color-text-secondary);
  font-style: italic;
}

/* Status column: dot + label or dropdown */
.status-cell {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.status-cell--editable .status-select {
  min-width: 120px;
  padding: 4px 8px;
  font-size: 0.9rem;
  border: 1px solid var(--color-border-main);
  border-radius: 6px;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  cursor: pointer;
}

.status-cell--editable .status-select:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.status-dot {
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.status-dot--active {
  background-color: var(--color-success-main);
}

.status-dot--in-progress {
  background-color: var(--color-warning-main);
}

.status-dot--inactive {
  background-color: var(--color-error-main);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border-light);
}

.page-button {
  padding: 8px 16px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all var(--transition-normal);
}

.page-button:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

.page-button:disabled {
  background-color: var(--color-border-light);
  color: var(--color-text-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.page-info {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
}

/* Courses filters */
.courses-filters {
  margin-bottom: 20px;
}

.courses-search-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.courses-search-row label {
  color: var(--color-text-primary);
  font-weight: 600;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.courses-search-input {
  flex: 1;
  max-width: 400px;
  padding: 8px 12px;
  border: 2px solid var(--color-border-main);
  border-radius: 6px;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.courses-search-button {
  padding: 8px 16px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all var(--transition-normal);
}

.courses-search-button:hover {
  background-color: var(--color-primary-dark);
}

.courses-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
}

.status-filter-section,
.activity-type-filter-section,
.provider-filter-section {
  display: flex;
  align-items: center;
  gap: 10px;
}

.courses-filters label {
  color: var(--color-text-primary);
  font-weight: 600;
  font-size: 0.95rem;
}

.status-filter-select,
.filter-select {
  padding: 8px 12px;
  border: 2px solid var(--color-border-main);
  border-radius: 6px;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.activity-type-filter-section .filter-select,
.provider-filter-section .filter-select {
  min-width: 220px;
}

/* Create New Section */
.create-new-section {
  margin-bottom: 20px;
  display: flex;
  justify-content: flex-end;
}

.create-new-button {
  padding: 10px 20px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all var(--transition-normal);
}

.create-new-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Actions Column */
.actions-column {
  text-align: center;
  width: 160px;
}

.actions-cell {
  text-align: center;
  padding: 12px 16px;
  white-space: nowrap;
}

.actions-cell .edit-button,
.actions-cell .delete-button {
  margin: 0 4px;
}

.edit-button {
  padding: 6px 12px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all var(--transition-normal);
}

.edit-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

.delete-button {
  padding: 6px 12px;
  background-color: var(--color-error-main, #c62828);
  color: var(--color-text-inverse, #fff);
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all var(--transition-normal);
}

.delete-button:hover {
  background-color: var(--color-error-dark, #b71c1c);
  transform: translateY(-1px);
}

/* CSV Import Section */
.csv-import-section {
  margin: 30px 0;
  padding: 30px;
  background-color: var(--color-surface-dark);
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
}

.csv-import-header h2 {
  color: var(--color-text-primary);
  font-size: 1.5rem;
  margin-bottom: 10px;
}

.csv-import-description {
  color: var(--color-text-secondary);
  font-size: 0.95rem;
  margin-bottom: 20px;
}

.csv-import-controls {
  display: flex;
  gap: 15px;
  align-items: center;
  margin-bottom: 15px;
}

.file-input {
  display: none;
}

.select-file-button {
  padding: 10px 20px;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-main);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all var(--transition-normal);
}

.select-file-button:hover:not(:disabled) {
  background-color: var(--color-surface-dark);
  border-color: var(--color-primary-main);
}

.select-file-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.import-button {
  padding: 10px 20px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all var(--transition-normal);
}

.import-button:hover:not(:disabled) {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

.import-button:disabled {
  background-color: var(--color-border-light);
  color: var(--color-text-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.selected-file {
  color: var(--color-text-secondary);
  font-size: 0.9rem;
  margin-bottom: 15px;
  font-style: italic;
}

.import-result {
  margin-top: 20px;
  padding: 20px;
  border-radius: 6px;
  border: 2px solid;
}

.import-result.success {
  background-color: rgba(76, 175, 80, 0.1);
  border-color: #4caf50;
  color: #2e7d32;
}

.import-result.error {
  background-color: rgba(244, 67, 54, 0.1);
  border-color: #f44336;
  color: #c62828;
}

.import-result h3 {
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1.2rem;
}

.import-result p {
  margin-bottom: 15px;
}

.import-errors {
  margin-top: 15px;
}

.import-errors h4 {
  margin-bottom: 10px;
  font-size: 1rem;
}

.import-errors ul {
  margin: 0;
  padding-left: 20px;
  max-height: 200px;
  overflow-y: auto;
}

.import-errors li {
  margin-bottom: 5px;
  font-size: 0.9rem;
}

.refresh-button {
  margin-top: 15px;
  padding: 8px 16px;
  background-color: var(--color-primary-main);
  color: var(--color-text-inverse);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all var(--transition-normal);
}

.refresh-button:hover {
  background-color: var(--color-primary-dark);
  transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 768px) {
  .admin-container {
    padding: 20px;
  }

  .tabs {
    flex-wrap: wrap;
  }

  .tab-button {
    padding: 10px 16px;
    font-size: 0.9rem;
  }

  .admin-table {
    font-size: 0.85rem;
  }

  .admin-table th,
  .admin-table td {
    padding: 8px 12px;
  }

  .pagination {
    flex-direction: column;
    gap: 15px;
  }

  .page-info {
    order: -1;
  }
}
</style>
